import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Loader2,
  Check,
  AlertTriangle,
  Minus,
  Camera,
  Trash2,
  User,
  Save,
  CheckCircle2,
  Fuel,
  Gauge,
  ClipboardCheck,
} from 'lucide-react';

const STATUS_BUTTONS = [
  { value: 'ok',    label: 'OK',    icon: Check,          color: 'bg-emerald-500 border-emerald-500 text-[var(--m-text)]',
    inactiveColor: 'border-[var(--m-border-light)] text-[var(--m-text-secondary)]' },
  { value: 'issue', label: 'Issue', icon: AlertTriangle,  color: 'bg-red-500 border-red-500 text-[var(--m-text)]',
    inactiveColor: 'border-[var(--m-border-light)] text-[var(--m-text-secondary)]' },
  { value: 'na',    label: 'N/A',   icon: Minus,          color: 'bg-gray-500 border-[var(--m-border-light)] text-[var(--m-text)]',
    inactiveColor: 'border-[var(--m-border-light)] text-[var(--m-text-secondary)]' },
];

const FUEL_LEVELS = ['Full', '3/4', '1/2', '1/4', 'Empty'];

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const MobileInspectionDetail = () => {
  const navigate = useNavigate();
  const { inspectionId } = useParams();
  const { api, operator } = useMobileAuth();

  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [checklist, setChecklist] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [notes, setNotes] = useState('');
  const [hoursMeter, setHoursMeter] = useState('');
  const [fuelLevel, setFuelLevel] = useState('');
  const [odometer, setOdometer] = useState('');
  const [customerName, setCustomerName] = useState('');

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const fetchInspection = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/mobile/hire-inspections/${inspectionId}`);
      const data = res.data;
      setInspection(data);
      setChecklist(data.checklist_items || []);
      setPhotos(data.photos || []);
      setNotes(data.notes || '');
      setHoursMeter(data.hours_meter != null ? String(data.hours_meter) : '');
      setFuelLevel(data.fuel_level || '');
      setOdometer(data.odometer != null ? String(data.odometer) : '');
      setCustomerName(data.customer_name || '');
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) toast.error('Not authorised');
      else if (status === 404) toast.error('Inspection not found');
      else toast.error('Failed to load inspection');
      navigate('/mobile/inspections');
    } finally {
      setLoading(false);
    }
  }, [api, inspectionId, navigate]);

  useEffect(() => {
    if (operator == null) return;  // wait for context hydration
    if (!operator.is_yard_dispatcher) {
      toast.error('Yard dispatcher role required.');
      navigate('/mobile/dashboard');
      return;
    }
    fetchInspection();
  }, [operator, fetchInspection, navigate]);

  // Canvas init
  useEffect(() => {
    if (!canvasRef.current || loading) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
  }, [loading]);

  const setItemStatus = (id, status) => {
    setChecklist(prev => prev.map(it => (it.id === id ? { ...it, status } : it)));
  };

  const setItemNotes = (id, val) => {
    setChecklist(prev => prev.map(it => (it.id === id ? { ...it, notes: val } : it)));
  };

  const addItemPhoto = async (id, file) => {
    try {
      const dataUrl = await fileToDataUrl(file);
      setChecklist(prev => prev.map(it =>
        it.id === id
          ? { ...it, photo_urls: [...(it.photo_urls || []), dataUrl] }
          : it
      ));
    } catch {
      toast.error('Failed to attach photo');
    }
  };

  const removeItemPhoto = (id, idx) => {
    setChecklist(prev => prev.map(it =>
      it.id === id
        ? { ...it, photo_urls: (it.photo_urls || []).filter((_, i) => i !== idx) }
        : it
    ));
  };

  const addGeneralPhoto = async (file) => {
    try {
      const dataUrl = await fileToDataUrl(file);
      setPhotos(prev => [...prev, dataUrl]);
    } catch {
      toast.error('Failed to attach photo');
    }
  };

  const removeGeneralPhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  // Signature canvas
  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const buildPayload = () => ({
    checklist_items: checklist,
    photos,
    notes,
    hours_meter: hoursMeter === '' ? null : Number(hoursMeter),
    fuel_level: fuelLevel || null,
    odometer: odometer === '' ? null : Number(odometer),
    customer_name: customerName || null,
  });

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      const payload = buildPayload();
      await api.put(`/api/mobile/hire-inspections/${inspectionId}`, payload);
      toast.success('Draft saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const pendingItems = checklist.filter(i => !i.status || i.status === 'pending');
    if (pendingItems.length) {
      toast.error(`${pendingItems.length} item(s) still unanswered`);
      return;
    }
    if (!customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }
    if (!hasSignature) {
      toast.error('Customer signature is required');
      return;
    }
    try {
      setSubmitting(true);
      const signatureData = canvasRef.current.toDataURL('image/png');
      const payload = { ...buildPayload(), signature_url: signatureData };
      await api.post(`/api/mobile/hire-inspections/${inspectionId}/submit`, payload);
      toast.success('Inspection submitted');
      navigate('/mobile/inspections');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !inspection) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  const job = inspection.job || {};
  const isPreHire = inspection.inspection_type === 'pre_hire';

  return (
    <div className="min-h-screen bg-[var(--m-bg)] pb-6" data-testid="mobile-inspection-detail-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[var(--m-bg-card)] border-b border-[var(--m-border)] p-4 safe-top">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/mobile/inspections')}
            className="text-[var(--m-text)] -ml-2"
            data-testid="inspection-back-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-[var(--m-text)] font-semibold flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-[#ffd108]" />
              {isPreHire ? 'Pre-Hire Inspection' : 'Post-Hire Inspection'}
            </h1>
            <p className="text-[var(--m-text-secondary)] text-xs">
              Job #{job.job_number || '—'} · {inspection.asset_equipment_type || job.equipment_type || 'Asset'}
            </p>
          </div>
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40 text-[10px]">DRY</Badge>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Checklist */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
              Condition Checklist
              <span className="text-[var(--m-text-muted)] text-xs">
                ({checklist.filter(i => i.status && i.status !== 'pending').length}/{checklist.length})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {checklist.map(item => (
              <ChecklistItemRow
                key={item.id}
                item={item}
                onStatusChange={(s) => setItemStatus(item.id, s)}
                onNotesChange={(v) => setItemNotes(item.id, v)}
                onAddPhoto={(f) => addItemPhoto(item.id, f)}
                onRemovePhoto={(idx) => removeItemPhoto(item.id, idx)}
              />
            ))}
          </CardContent>
        </Card>

        {/* Asset readings */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm">Asset Readings</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[var(--m-text-secondary)] text-xs flex items-center gap-1">
                <Gauge className="h-3 w-3" />Hours meter
              </Label>
              <Input
                type="number"
                step="0.1"
                value={hoursMeter}
                onChange={(e) => setHoursMeter(e.target.value)}
                className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1"
                data-testid="inspection-hours-meter"
                placeholder="0.0"
              />
            </div>
            <div>
              <Label className="text-[var(--m-text-secondary)] text-xs flex items-center gap-1">
                <Fuel className="h-3 w-3" />Fuel level
              </Label>
              <Select value={fuelLevel} onValueChange={setFuelLevel}>
                <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1" data-testid="inspection-fuel-level">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {FUEL_LEVELS.map(lv => (
                    <SelectItem key={lv} value={lv}>{lv}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-[var(--m-text-secondary)] text-xs">Odometer (optional)</Label>
              <Input
                type="number"
                step="1"
                value={odometer}
                onChange={(e) => setOdometer(e.target.value)}
                className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1"
                data-testid="inspection-odometer"
                placeholder="0"
              />
            </div>
          </CardContent>
        </Card>

        {/* General photos */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm">General Photos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p, idx) => (
                <div key={idx} className="relative group">
                  <img src={p} alt="" className="w-full h-24 object-cover rounded-md border border-[var(--m-border-light)]" />
                  <button
                    type="button"
                    onClick={() => removeGeneralPhoto(idx)}
                    className="absolute top-1 right-1 bg-red-500/80 text-[var(--m-text)] rounded-full p-0.5"
                    data-testid={`remove-general-photo-${idx}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <label className="h-24 border-2 border-dashed border-[var(--m-border-light)] rounded-md flex flex-col items-center justify-center text-[var(--m-text-muted)] cursor-pointer hover:border-[#ffd108] hover:text-[#ffd108]">
                <Camera className="h-5 w-5 mb-1" />
                <span className="text-xs">Add</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  data-testid="general-photo-input"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) addGeneralPhoto(f);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm">Inspector Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional observations, damage details, follow-ups…"
              rows={3}
              className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
              data-testid="inspection-notes"
            />
          </CardContent>
        </Card>

        {/* Customer signature */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm">Customer Sign-Off</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-[var(--m-text-secondary)] text-xs">Customer name <span className="text-red-500">*</span></Label>
              <div className="flex items-center gap-2 mt-1">
                <User className="h-4 w-4 text-[var(--m-text-muted)]" />
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Name of person signing"
                  className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                  data-testid="inspection-customer-name"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[var(--m-text-secondary)] text-xs">Signature <span className="text-red-500">*</span></Label>
                {hasSignature && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSignature}
                    className="text-red-400 h-6 px-2"
                    data-testid="clear-signature-btn"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              <div className="border border-[var(--m-border-light)] rounded-lg overflow-hidden">
                <canvas
                  ref={canvasRef}
                  width={300}
                  height={150}
                  className="w-full touch-none"
                  style={{ background: '#ffffff' }}
                  data-testid="inspection-signature-canvas"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
              <p className="text-[var(--m-text-muted)] text-xs mt-1 text-center">Draw signature above</p>
            </div>
          </CardContent>
        </Card>

        {/* Save / Submit Actions — inline at end of inspection */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 h-12 border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[var(--m-bg-elevated)]"
            onClick={handleSaveDraft}
            disabled={saving || submitting}
            data-testid="save-draft-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Draft
          </Button>
          <Button
            className="flex-1 h-12 btn-volt"
            onClick={handleSubmit}
            disabled={saving || submitting}
            data-testid="submit-inspection-btn"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Submit
          </Button>
        </div>
      </div>
    </div>
  );
};

const ChecklistItemRow = ({ item, onStatusChange, onNotesChange, onAddPhoto, onRemovePhoto }) => {
  const [showNotes, setShowNotes] = useState(!!item.notes);
  return (
    <div className="border border-[var(--m-border)] rounded-lg p-3 bg-[var(--m-bg)]" data-testid={`checklist-item-${item.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1">
          {item.category && (
            <span className="text-[10px] uppercase tracking-wide text-[var(--m-text-muted)]">{item.category}</span>
          )}
          <p className="text-[var(--m-text)] text-sm">{item.label}</p>
        </div>
      </div>
      <div className="flex gap-1.5 mb-2">
        {STATUS_BUTTONS.map(btn => {
          const Icon = btn.icon;
          const active = item.status === btn.value;
          return (
            <button
              key={btn.value}
              type="button"
              onClick={() => onStatusChange(btn.value)}
              data-testid={`item-${item.id}-status-${btn.value}`}
              className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-xs font-medium border transition-colors ${active ? btn.color : btn.inactiveColor}`}
            >
              <Icon className="h-3 w-3" />
              {btn.label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setShowNotes(v => !v)}
          className="text-xs text-[#ffd108] hover:underline"
          data-testid={`item-${item.id}-toggle-notes`}
        >
          {showNotes ? 'Hide notes' : 'Add notes'}
        </button>
        <label className="text-xs text-[#ffd108] hover:underline cursor-pointer">
          + Photo
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid={`item-${item.id}-photo-input`}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onAddPhoto(f);
              e.target.value = '';
            }}
          />
        </label>
      </div>

      {showNotes && (
        <Textarea
          value={item.notes || ''}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Details…"
          rows={2}
          className="bg-[var(--m-bg-card)] border-[var(--m-border-light)] text-[var(--m-text)] mt-2 text-sm"
          data-testid={`item-${item.id}-notes`}
        />
      )}

      {item.photo_urls && item.photo_urls.length > 0 && (
        <div className="grid grid-cols-4 gap-1.5 mt-2">
          {item.photo_urls.map((p, idx) => (
            <div key={idx} className="relative">
              <img src={p} alt="" className="w-full h-14 object-cover rounded border border-[var(--m-border-light)]" />
              <button
                type="button"
                onClick={() => onRemovePhoto(idx)}
                className="absolute top-0.5 right-0.5 bg-red-500/80 text-[var(--m-text)] rounded-full p-0.5"
              >
                <Trash2 className="h-2.5 w-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileInspectionDetail;
